import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScaleIcon, ClockIcon, RefreshCwIcon, TruckIcon, HourglassIcon, FileCheck2Icon, WeightIcon, PlusIcon } from "lucide-react";
import { FINALIDADES, FIN_COR } from "@/components/EtapaOperacao";
import { HistoricoVeiculos } from "@/components/HistoricoVeiculos";
import { useBalancaSerial } from "@/hooks/useBalancaSerial";
import { IndicadorBalanca } from "@/components/balanca/IndicadorBalanca";
import { PesagemModal } from "@/components/balanca/PesagemModal";
import { PesagemAvulsaModal } from "@/components/balanca/PesagemAvulsaModal";
import { Button } from "@/components/ui/button";
import { LiberadasTable, TicketsTable } from "@/components/balanca/BalancaTabelas";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const elapsed = (iso, now) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
};
const hoje = () => new Date().toISOString().slice(0, 10);

const Kpi = ({ icon: Icon, label, value, sub, cor, testid }) => (
  <Card className="bg-[#14151A] border-border rounded-none p-4 flex items-center justify-between" data-testid={testid}>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-black mt-1" style={{ color: cor }}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
    <div className="p-3 border" style={{ color: cor, borderColor: `${cor}55`, background: `${cor}14` }}><Icon className="w-5 h-5" /></div>
  </Card>
);

/* ============ BALANÇA RODOVIÁRIA: pesagem inicial/final das carretas liberadas pela Portaria ============ */
export default function Balanca() {
  const { user } = useAuth();
  const serial = useBalancaSerial();
  const [liberadas, setLiberadas] = useState([]);
  const [fila, setFila] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filtros, setFiltros] = useState({ q: "", de: "", ate: "", status: "" });
  const [now, setNow] = useState(new Date());
  const [sync, setSync] = useState(null);
  const [pesagem, setPesagem] = useState({ open: false, modo: 1, op: null, ticket: null });
  const [avulsaOpen, setAvulsaOpen] = useState(false);
  const abrirTicket = (t) => window.open(`/balanca/ticket/${t.id}`, "_blank", "noopener");

  const loadTickets = async () => {
    const params = {};
    if (filtros.q) params.q = filtros.q;
    if (filtros.de) params.de = `${filtros.de}T00:00:00`;
    if (filtros.ate) params.ate = `${filtros.ate}T23:59:59.999`;
    if (filtros.status) params.status = filtros.status;
    try { setTickets((await api.get("/balanca/pesagens", { params })).data); } catch { /* mantém */ }
  };
  const load = async () => {
    try {
      const [l, ag, k] = await Promise.all([api.get("/balanca/liberadas"), api.get("/portaria/aguardando"), api.get("/balanca/kpis")]);
      setLiberadas(l.data); setFila(ag.data); setKpis(k.data); setSync(new Date());
    } catch { /* mantém último estado */ }
  };
  const reload = () => { load(); loadTickets(); };

  useEffect(() => {
    reload();
    const poll = setInterval(load, 5000);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const aguardando = useMemo(() => fila.filter((f) => f.status === "AGUARDANDO"), [fila]);
  const podeCancelar = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="p-6 space-y-6" data-testid="balanca-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] flex items-center gap-2"><ScaleIcon className="w-4 h-4" /> Balança Rodoviária</div>
          <h1 className="font-display text-4xl font-black mt-1">Pesagem <span className="text-[#FFD60A]">Inicial & Final</span></h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            Quando a Portaria libera a carreta para entrar, ela aparece aqui. Faça a <b className="text-[#FFD60A]">1ª pesagem</b> (ou marque que
            <b className="text-white"> não passa na balança</b>) e, antes de sair, a <b className="text-[#34C759]">2ª pesagem</b> — o líquido é calculado e o ticket emitido.
            Carreta com 1ª pesagem feita <b className="text-[#FF3B30]">não sai</b> da Portaria sem a 2ª.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button onClick={() => setAvulsaOpen(true)} data-testid="btn-pesagem-avulsa"
                  className="rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 font-bold uppercase tracking-wider text-xs h-10">
            <PlusIcon className="w-4 h-4 mr-2" /> Nova pesagem avulsa
          </Button>
          <div className="text-right text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <RefreshCwIcon className="w-3 h-3" /> Atualiza a cada 5s {sync && <span className="font-mono">· {sync.toLocaleTimeString("pt-BR")}</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={TruckIcon} label="Liberadas na base" value={kpis?.liberadas ?? "—"} sub={`${kpis?.aguardando_decisao ?? 0} aguardando decisão`} cor="#00E5FF" testid="kpi-liberadas" />
        <Kpi icon={HourglassIcon} label="Aguardando 2ª pesagem" value={kpis?.aguardando_2a ?? "—"} sub="tickets abertos" cor="#FFD60A" testid="kpi-abertos" />
        <Kpi icon={FileCheck2Icon} label="Finalizadas hoje" value={kpis?.finalizadas_hoje ?? "—"} sub="tickets emitidos" cor="#34C759" testid="kpi-finalizadas" />
        <Kpi icon={WeightIcon} label="Líquido hoje" value={kpis ? `${(kpis.liquido_hoje_kg / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t` : "—"} sub="movimentação do dia" cor="#FF9F0A" testid="kpi-liquido" />
      </div>

      <IndicadorBalanca serial={serial} />

      <LiberadasTable itens={liberadas} now={now} reload={reload}
                      onPesar1={(op) => setPesagem({ open: true, modo: 1, op, ticket: null })}
                      onPesar2={(t) => setPesagem({ open: true, modo: 2, op: null, ticket: t })}
                      onTicket={abrirTicket} />

      <TicketsTable tickets={tickets} filtros={filtros} setFiltros={setFiltros} onBuscar={loadTickets} reload={reload} podeCancelar={podeCancelar}
                    onTicket={abrirTicket} onPesar2={(t) => setPesagem({ open: true, modo: 2, op: null, ticket: t })} />

      <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
        <div className="p-4 border-b border-border text-[11px] uppercase tracking-[0.3em] text-[#7DD3FC] flex items-center gap-2">
          <ClockIcon className="w-4 h-4" /> Fila de espera do lado de fora ({aguardando.length})
        </div>
        <table className="w-full text-sm" data-testid="balanca-tabela-fila">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="px-4 py-2">Placas</th><th className="px-4 py-2">Motorista</th><th className="px-4 py-2">Empresa</th><th className="px-4 py-2">Finalidade</th><th className="px-4 py-2">Chegou</th>
            </tr>
          </thead>
          <tbody>
            {aguardando.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-xs">Sem veículos aguardando.</td></tr>}
            {aguardando.map((a) => (
              <tr key={a.id} className="border-b border-border/50" data-testid={`balanca-fila-${a.id}`}>
                <td className="px-4 py-2 font-mono text-[11px] text-[#FFD60A]">{a.placa_cavalo || "—"} / {a.placa_carreta || "—"}{a.frota && <span className="text-[#00E5FF]"> · FROTA {a.frota}</span>}</td>
                <td className="px-4 py-2 font-semibold text-white">{a.nome}</td>
                <td className="px-4 py-2 text-xs">{a.empresa || "—"}</td>
                <td className="px-4 py-2"><div className="flex gap-1 flex-wrap">{(a.finalidades || [a.finalidade]).map((f) => (
                  <Badge key={f} className="rounded-none text-[9px] border" style={{ color: FIN_COR[f], borderColor: `${FIN_COR[f]}66`, background: `${FIN_COR[f]}1A` }}>{FINALIDADES[f]}</Badge>))}</div></td>
                <td className="px-4 py-2 font-mono text-xs">{fmtDT(a.chegou_em)} <span className="text-muted-foreground">· {elapsed(a.chegou_em, now)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <HistoricoVeiculos />

      <PesagemModal open={pesagem.open} onOpenChange={(v) => setPesagem((p) => ({ ...p, open: v }))} modo={pesagem.modo} op={pesagem.op} ticket={pesagem.ticket}
                    serial={serial} onSaved={(t) => { reload(); if (t?.status === "FINALIZADO") abrirTicket(t); }} />
      <PesagemAvulsaModal open={avulsaOpen} onOpenChange={setAvulsaOpen} serial={serial} onSaved={reload} />
    </div>
  );
}
